import React, { FC } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import {
  ConfirmationPopup,
  Footer,
  Header,
  NotificationBanner,
  useAlertPopupConfig,
  useDismissAlertPopup,
  AuthStore,
  INavigationItem,
  ConfirmationPopupProps,
  ConfirmationPopupState,
} from '@dew21/ui';
import styled from 'styled-components';
import { QuestionnaireRouter } from '../questionnaires/QuestionnaireRouter';
import { LoginRoute } from './LoginRoute';
import { useLocation } from 'react-router-dom';
import { NotFound } from './NotFound';
import { AuthGuard } from './AuthGuard';
import { CustomersRouter } from '../customers/CustomersRouter';
import { UsersRouter } from '../users/UsersRouter';
import { useRecoilValue } from 'recoil';
import Axios from 'axios';
import { ResetPasswordRoute } from './ResetPasswordRoute';
import { ProductRouter } from '../products/ProductRouter';
import { Leads } from '../leads/Leads';
import { OperationsRouter } from '../operations/OperationsRouter';
import { LeadsRouter } from '../leads/LeadsRouter';

export const APP_PORTAL_NAME = 'Administration';

const ViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  position: relative;
  overflow: auto;

  min-height: 100vh;

  & > * {
    flex: 1;
  }

  header, footer {
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: auto;
  }
`;

export const RouterView: FC = (props) => {
  const location = useLocation();
  const notificationBannerConfig = useAlertPopupConfig();
  const closeNotificationBanner = useDismissAlertPopup();
  const confirmationPopupState = useRecoilValue<ConfirmationPopupProps>(ConfirmationPopupState);


  const isLoginRoute = () => {
    return location?.pathname.includes('login') || location?.pathname.includes('password');
  };

  const NavigationItems: INavigationItem[] = [
    { text: 'Benutzer', url: '/users', icon: 'user' },
    { text: 'Kunden', url: '/customers', icon: 'users' },
    { text: 'Produkte', url: '/products', icon: 'gas' },
    { text: 'Leads', url: '/leads', icon: 'warn' },
    { text: 'Vorgänge', url: '/operations', icon: 'warn' },
    { text: 'Fragebögen', url: '/questionnaires', icon: 'add-document' },
  ];

  return (
    <ViewWrapper>
      {
        !isLoginRoute() &&
        <Header
          appName={APP_PORTAL_NAME}
          items={NavigationItems}
          interceptLogout={async () => {
            const tokens = AuthStore.getSession()?.tokens;
            await Axios.post('/api/auth/logout', { tokens }, AuthStore.getAuthHeader()).catch(() => undefined);
            return true;    // allow logout
          }}
        />
      }
      <NotificationBanner
        open={notificationBannerConfig !== null}
        onClose={() => closeNotificationBanner()}
        renderContent={notificationBannerConfig?.renderContent}
        error={notificationBannerConfig?.error}/>
      <Switch>
        <Route path={'/reset-password/:token'}>
          <ResetPasswordRoute/>
        </Route>
        <Route path={'/login'}>
          <LoginRoute/>
        </Route>
        <Route exact path={'/'}>
          <Redirect to={'/users'}/>
        </Route>
        <Route path={'/users'}>
          <AuthGuard>
            <UsersRouter/>
          </AuthGuard>
        </Route>
        <Route path={'/customers'}>
          <AuthGuard>
            <CustomersRouter/>
          </AuthGuard>
        </Route>
        <Route path={'/products'}>
          <AuthGuard>
            <ProductRouter/>
          </AuthGuard>
        </Route>
        <Route path={'/leads'}>
          <AuthGuard>
            <LeadsRouter/>
          </AuthGuard>
        </Route>
        <Route path={'/operations'}>
          <AuthGuard>
            <OperationsRouter/>
          </AuthGuard>
        </Route>
        <Route path={'/questionnaires'}>
          <AuthGuard>
            <QuestionnaireRouter/>
          </AuthGuard>
        </Route>
        <Route path="/404">
          <NotFound/>
        </Route>
        <Route path='*'>
          <Redirect to="/404"/>
        </Route>
        }
      </Switch>
      {
        !isLoginRoute() &&
        <Footer/>
      }
      <ConfirmationPopup config={confirmationPopupState?.config}/>
    </ViewWrapper>
  );
};
